import { useWorkflowGraphState } from "#src/batteries-included-components/Graph/WorkflowGraph/WorkflowGraphCanvas/WorkflowGraphStore";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  CheckboxInput,
  DateSelectorInputBare,
  DropdownInput,
  Form,
  FormLabel,
  RadioInput,
  useForm,
  VariableTextInputBare,
} from "@validereinc/common-components";
import { AssetType, ReportingGroupAdapter } from "@validereinc/domain";
import {
  toTitleCaseString,
  yearMonthFormatter,
  yearMonthToDate,
} from "@validereinc/utilities";
import classNames from "classnames/bind";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  RunCalculationsFormValuesType,
  useWorkflowConfigurationFormStore,
} from "../WorkflowConfigurationFormStore";
import styles from "./WorkflowConfigurationRunCalculationsDetails.module.scss";

const cx = classNames.bind(styles);

const AssetTypeDropdownOptions = Object.values(AssetType).map((assetType) => ({
  label: toTitleCaseString(assetType, { intent: "ui" }),
  value: assetType,
}));

const monthModeOptions = [
  { label: "Specific Months", value: "specific" },
  { label: "Month Range", value: "range" },
];

const formDefaultValues = {
  entity_type: AssetType.FACILITY,
  reporting_group_id: "",
  month_mode: "specific" as const,
  specific_months: [""],
  promote_to_record: false,
};

// Utility functions
const isVariableValue = (value: string): boolean => {
  return value.includes("$");
};

const WorkflowConfigurationRunCalculationsDetails = ({
  selectedNodeId,
}: {
  selectedNodeId?: string;
}) => {
  const {
    setRunCalculations,
    setRunCalculationsIsValid,
    getRunCalculationsFormValues,
  } = useWorkflowConfigurationFormStore();

  const { getAvailableStepVariables } = useWorkflowGraphState();

  const storeValues = getRunCalculationsFormValues();

  const form = useForm<RunCalculationsFormValuesType>({
    values: storeValues ?? formDefaultValues,
  });

  const formValues = form.watch();
  const monthMode = formValues.month_mode;

  const [specificMonths, setSpecificMonths] = useState<string[]>([]);

  useEffect(() => {
    if (
      Array.isArray(storeValues?.specific_months) &&
      storeValues?.specific_months.length > 0
    ) {
      setSpecificMonths(storeValues?.specific_months);
    }
  }, [JSON.stringify(storeValues?.specific_months)]);

  // Track input type for each month (variable vs date)
  const [monthInputTypes, setMonthInputTypes] = useState<
    Array<"variable" | "date">
  >(() => {
    const initialTypes = (storeValues?.specific_months ?? [""]).map((value) =>
      isVariableValue(value) ? "variable" : "date"
    );
    return initialTypes.length > 0 ? initialTypes : ["date"];
  });

  const reportingGroupsQuery = useQuery({
    queryKey: ["reportingGroups"],
    queryFn: () => ReportingGroupAdapter.getList({}),
  });

  const availableVariables = useMemo(
    () => (selectedNodeId ? getAvailableStepVariables(selectedNodeId) : []),
    [getAvailableStepVariables, selectedNodeId]
  );

  // Create the final form values to send to store
  const finalFormValues = useMemo(() => {
    const baseValues = {
      entity_type: formValues.entity_type,
      reporting_group_id: formValues.reporting_group_id,
      month_mode: formValues.month_mode,
      promote_to_record: formValues.promote_to_record,
    };

    if (formValues.month_mode === "specific") {
      return {
        ...baseValues,
        specific_months: specificMonths.filter((m) => m.trim() !== ""),
      };
    } else {
      return {
        ...baseValues,
        start_month: formValues.start_month,
        end_month: formValues.end_month,
        fill_year_months: true,
      };
    }
  }, [JSON.stringify(formValues), specificMonths]);

  useEffect(() => {
    setRunCalculations(finalFormValues);
  }, [JSON.stringify(finalFormValues), setRunCalculations]);

  // Update validation
  useEffect(() => {
    const isValid = Boolean(
      finalFormValues.entity_type &&
        finalFormValues.reporting_group_id &&
        (finalFormValues.month_mode === "specific"
          ? specificMonths.length > 0 &&
            specificMonths.every((month) => month.trim() !== "")
          : (finalFormValues as any).start_month &&
            (finalFormValues as any).end_month)
    );
    setRunCalculationsIsValid(isValid);
  }, [finalFormValues, setRunCalculationsIsValid, specificMonths]);

  const addMonth = useCallback(() => {
    setSpecificMonths((prev) => [...prev, ""]);
    setMonthInputTypes((prev) => [...prev, "date"]);
  }, []);

  const removeMonth = useCallback((index: number) => {
    setSpecificMonths((prev) =>
      prev.length > 1 ? prev.filter((_, i) => i !== index) : prev
    );
    setMonthInputTypes((prev) =>
      prev.length > 1 ? prev.filter((_, i) => i !== index) : prev
    );
  }, []);

  const updateMonth = useCallback((index: number, value: string) => {
    setSpecificMonths((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  }, []);

  const toggleInputType = useCallback(
    (index: number) => {
      setMonthInputTypes((prev) => {
        const updated = [...prev];
        updated[index] = updated[index] === "variable" ? "date" : "variable";
        return updated;
      });
      // Clear value when switching input type
      updateMonth(index, "");
    },
    [updateMonth]
  );

  const handleDateChange = useCallback(
    (index: number, date: Date | undefined) => {
      if (date) {
        updateMonth(index, yearMonthFormatter(date));
      } else {
        updateMonth(index, "");
      }
    },
    [updateMonth]
  );

  const handleRangeDateChange = useCallback(
    (field: "start" | "end", date: Date | undefined) => {
      if (date) {
        form.setValue(
          field === "start" ? "start_month" : "end_month",
          yearMonthFormatter(date)
        );
      } else {
        form.setValue(field === "start" ? "start_month" : "end_month", "");
      }
    },
    [form]
  );

  return (
    <Form {...form}>
      <div className={cx("container")}>
        <DropdownInput
          name="entity_type"
          label="Entity Type"
          className={cx("entity-type-input")}
          labelKey="label"
          valueKey="value"
          options={AssetTypeDropdownOptions}
          isRequired
        />

        <DropdownInput
          name="reporting_group_id"
          label="Reporting Scenario"
          className={cx("reporting-group-input")}
          labelKey="name"
          valueKey="id"
          options={reportingGroupsQuery.data?.data ?? []}
          isLoading={reportingGroupsQuery.isLoading}
          isRequired
        />

        <RadioInput
          name="month_mode"
          label="Month Selection Mode"
          options={monthModeOptions}
          className={cx("month-mode-input")}
          valueKey="value"
          labelKey="label"
        />

        {monthMode === "specific" && (
          <div className={cx("specific-months-section")}>
            <label className={cx("section-label")}>Specific Months</label>
            <div className={cx("months-list")}>
              {specificMonths.map((month, index) => (
                <div
                  key={index}
                  className={cx("month-input-row")}
                >
                  <div className={cx("month-input")}>
                    {monthInputTypes[index] === "variable" ? (
                      <VariableTextInputBare
                        name={`specific_months.${index}`}
                        value={month}
                        onChange={(value) => updateMonth(index, value)}
                        placeholder="$VARIABLE"
                        suggestions={availableVariables}
                        type="text"
                        className={cx("variable-input")}
                      />
                    ) : (
                      <DateSelectorInputBare
                        name={`specific_months.${index}`}
                        variant="month"
                        value={yearMonthToDate(month)}
                        onChange={(date) => {
                          if (typeof date === "undefined") return;
                          const singleDateValue =
                            date instanceof Date ? date : date.from;
                          handleDateChange(index, singleDateValue);
                        }}
                        placeholder="Select month"
                        className={cx("date-input")}
                        isRange={false}
                        isArraySyntax={false}
                      />
                    )}
                  </div>

                  <Button
                    type="button"
                    variant="ghost"
                    size="small"
                    icon="swap"
                    onClick={() => toggleInputType(index)}
                    className={cx("toggle-button")}
                    title={`Switch to ${monthInputTypes[index] === "variable" ? "date picker" : "variable input"}`}
                  />

                  {specificMonths.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="small"
                      icon="trash"
                      onClick={() => removeMonth(index)}
                      className={cx("remove-button")}
                    />
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="ghost"
                size="small"
                icon="plus"
                onClick={addMonth}
                className={cx("add-button")}
              >
                Add Month
              </Button>
            </div>
          </div>
        )}

        {monthMode === "range" && (
          <div className={cx("range-section")}>
            <label className={cx("section-label")}>Month Range</label>
            <p className={cx("range-description")}>
              Define start and end months. The system will automatically run
              calculations for all months between these bounds (inclusive).
            </p>
            <div className={cx("range-inputs")}>
              <div className={cx("range-input-container")}>
                <FormLabel
                  inputId="start_month"
                  label="Start Month"
                  isRequired
                />
                <DateSelectorInputBare
                  name="start_month"
                  variant="month"
                  value={yearMonthToDate(formValues.start_month ?? "")}
                  onChange={(date) => {
                    if (typeof date === "undefined") return;
                    const singleDateValue =
                      date instanceof Date ? date : date.from;
                    handleRangeDateChange("start", singleDateValue);
                  }}
                  placeholder="Select start month"
                  className={cx("date-input")}
                  isRange={false}
                  isArraySyntax={false}
                  style={{ width: "100%" }}
                />
              </div>
              <div className={cx("range-input-container")}>
                <FormLabel
                  inputId="end_month"
                  label="End Month"
                  isRequired
                />
                <DateSelectorInputBare
                  name="end_month"
                  variant="month"
                  value={yearMonthToDate(formValues.end_month ?? "")}
                  onChange={(date) => {
                    if (typeof date === "undefined") return;
                    const singleDateValue =
                      date instanceof Date ? date : date.from;
                    handleRangeDateChange("end", singleDateValue);
                  }}
                  placeholder="Select end month"
                  className={cx("date-input")}
                  isRange={false}
                  isArraySyntax={false}
                  style={{ width: "100%" }}
                  min={yearMonthToDate(formValues.start_month ?? "")?.from}
                />
              </div>
            </div>
          </div>
        )}

        <CheckboxInput
          name="promote_to_record"
          label="Promote to Record"
          className={cx("promote-checkbox")}
          isRequired
          isLabelShown={false}
        />
      </div>
    </Form>
  );
};

export default WorkflowConfigurationRunCalculationsDetails;
